/**
 * Estado y acciones de la pestaña "Cotizaciones" del Admin.
 */
import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import {
  fetchQuotes as apiFetchQuotes,
  updateQuote as apiUpdateQuote,
  updateQuoteStatus as apiUpdateQuoteStatus,
  deleteQuote as apiDeleteQuote,
  convertQuoteToContract as apiConvertQuoteToContract,
  fetchPdfTextos as apiFetchPdfTextos,
  updatePdfTextos as apiUpdatePdfTextos,
} from "./api";
import { filterQuotes, sortByCreatedDesc, emptyQuoteEditForm, quoteFormToPayload, buildQuoteWhatsAppText } from "./utils";
import { generateQuotePDF } from "./pdf/quotePDF";

export function useQuotes(pricing, enabled = true, onContractCreated) {
  const [quotes, setQuotes] = useState([]);
  const [quotesSearch, setQuotesSearch] = useState("");
  const [loadingQuotes, setLoadingQuotes] = useState(false);
  const [pdfTextos, setPdfTextos] = useState(null);
  const [savingPdfTextos, setSavingPdfTextos] = useState(false);
  const [editingQuote, setEditingQuote] = useState(null); // { id, folio, form }

  const fetchQuotesData = useCallback(async () => {
    setLoadingQuotes(true);
    try {
      const res = await apiFetchQuotes();
      setQuotes(res.data);
    } catch (e) {
      toast.error("Error al cargar cotizaciones");
    } finally {
      setLoadingQuotes(false);
    }
  }, []);

  const fetchPdfTextosData = useCallback(async () => {
    try {
      const res = await apiFetchPdfTextos();
      setPdfTextos(res.data);
    } catch (e) {
      console.error("Error cargando textos del PDF:", e);
    }
  }, []);

  useEffect(() => {
    if (enabled) { fetchQuotesData(); fetchPdfTextosData(); }
  }, [enabled, fetchQuotesData, fetchPdfTextosData]);

  const mergedTextos = () => ({ ...(pdfTextos || {}), nota_precios: pricing?.nota_precios || pdfTextos?.nota_precios });

  const viewPdf = (quote) => generateQuotePDF(quote, mergedTextos());

  const sendWhatsApp = (quote) => {
    const text = buildQuoteWhatsAppText(quote, mergedTextos());
    const phone = (quote.telefono || "").replace(/\D/g, "");
    const url = phone ? `https://wa.me/52${phone}?text=${encodeURIComponent(text)}` : `https://wa.me/?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank");
  };

  const startEdit = (quote) => setEditingQuote({ id: quote.id, folio: quote.folio, created_at: quote.created_at, form: emptyQuoteEditForm(quote) });
  const cancelEdit = () => setEditingQuote(null);
  const setEditForm = (updater) =>
    setEditingQuote((prev) => (prev ? { ...prev, form: typeof updater === "function" ? updater(prev.form) : updater } : prev));

  const saveEdit = async () => {
    if (!editingQuote) return;
    try {
      await apiUpdateQuote(editingQuote.id, quoteFormToPayload(editingQuote.form));
      toast.success("Cotización actualizada");
      setEditingQuote(null);
      fetchQuotesData();
    } catch (e) {
      toast.error("Error al actualizar la cotización");
    }
  };

  const regeneratePdfAfterEdit = async () => {
    if (!editingQuote) return;
    const payload = quoteFormToPayload(editingQuote.form);
    await generateQuotePDF({ ...payload, folio: editingQuote.folio, created_at: editingQuote.created_at }, mergedTextos());
  };

  const changeStatus = async (quoteId, status) => {
    try {
      await apiUpdateQuoteStatus(quoteId, status);
      setQuotes((prev) => prev.map((q) => (q.id === quoteId ? { ...q, status } : q)));
      toast.success("Estado actualizado");
    } catch (e) {
      toast.error("Error al actualizar el estado");
    }
  };

  const removeQuote = async (quoteId) => {
    if (!window.confirm("¿Eliminar esta cotización? Esta acción no se puede deshacer.")) return;
    try {
      await apiDeleteQuote(quoteId);
      toast.success("Cotización eliminada");
      fetchQuotesData();
    } catch (e) {
      toast.error("Error al eliminar la cotización");
    }
  };

  const convertToContract = async (quoteId) => {
    if (!window.confirm("¿Convertir esta cotización en un contrato nuevo? Se creará con el siguiente folio PIC.")) return;
    try {
      const res = await apiConvertQuoteToContract(quoteId);
      toast.success(`Contrato ${res.data.folio} creado a partir de la cotización`);
      fetchQuotesData();
      if (onContractCreated) onContractCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error al convertir en contrato");
    }
  };

  const savePdfTextos = async () => {
    if (!pdfTextos) return;
    setSavingPdfTextos(true);
    try {
      const res = await apiUpdatePdfTextos(pdfTextos);
      setPdfTextos(res.data);
      toast.success("Textos del PDF actualizados");
    } catch (e) {
      toast.error("Error al guardar los textos del PDF");
    } finally {
      setSavingPdfTextos(false);
    }
  };

  const filteredQuotes = () => filterQuotes(quotes, quotesSearch).sort(sortByCreatedDesc);

  return {
    quotes, loadingQuotes, quotesSearch, setQuotesSearch, filteredQuotes,
    pdfTextos, setPdfTextos, savingPdfTextos, savePdfTextos,
    editingQuote, startEdit, cancelEdit, setEditForm, saveEdit, regeneratePdfAfterEdit,
    viewPdf, sendWhatsApp, changeStatus, removeQuote, convertToContract,
    fetchQuotesData,
  };
}
